/**
 * Bússola do HUD (F81, mockup ui-b): partes puras. O rumo é horário a partir do norte, como no mapa: o
 * norte é −z (o alto da tela do mapa) e o leste é +x. A janela mostra 180° (de oeste a leste olhando para o
 * norte, como no mockup) e a faixa de marcas cobre de −90° a 450°, para qualquer rumo de 0° a 360° caber
 * nela inteira; o DOM só desloca a faixa (`compassOffset`).
 */

const TAU = Math.PI * 2;

/** Ângulo em [0, 2π). */
export const wrapAngle = (a: number): number => ((a % TAU) + TAU) % TAU;

/** Diferença assinada `a − b` em (−π, π]. */
export function angleDiff(a: number, b: number): number {
  const d = wrapAngle(a - b);
  return d > Math.PI ? d - TAU : d;
}

/** Rumo (rad, [0, 2π)) de uma direção no plano: 0 = norte (−z), π/2 = leste (+x). */
export const bearing = (dx: number, dz: number): number => wrapAngle(Math.atan2(dx, -dz));

/** Metade da janela da bússola (90°). */
export const COMPASS_HALF = Math.PI / 2;
/** Onde fica o alvo fora da janela: perto da borda, antes do esmaecido das pontas. */
export const COMPASS_EDGE = 0.86;
/** Passo das marcas (22,5°: três pontos entre as letras, como no mockup). */
export const COMPASS_STEP_DEG = 22.5;
/** A faixa de marcas: de −90° a 450° (três janelas). */
export const COMPASS_FROM_DEG = -90;
export const COMPASS_SPAN_DEG = 540;

/** Pontos cardeais em português (N, L, S, O) e os colaterais. */
const CARDINALS: Record<number, string> = { 0: "N", 45: "NE", 90: "L", 135: "SE", 180: "S", 225: "SO", 270: "O", 315: "NO" };

export interface CompassTick {
  /** Rumo da marca em graus (pode passar de 360 ou ficar abaixo de 0: a faixa repete). */
  deg: number;
  /** Posição na faixa, de 0 (−90°) a 1 (450°). */
  at: number;
  /** "N", "L", "S", "O" nos cardeais; "" nos pontos. */
  label: string;
  /** Cardeal (letra grande) ou colateral (ponto maior). */
  kind: "cardeal" | "colateral" | "ponto";
}

/** As marcas da faixa, de 22,5° em 22,5° (a faixa é feita uma vez; só o deslocamento muda). */
export function compassTicks(): CompassTick[] {
  const ticks: CompassTick[] = [];
  for (let deg = COMPASS_FROM_DEG; deg <= COMPASS_FROM_DEG + COMPASS_SPAN_DEG; deg += COMPASS_STEP_DEG) {
    const norm = ((deg % 360) + 360) % 360;
    const kind = norm % 90 === 0 ? "cardeal" : norm % 45 === 0 ? "colateral" : "ponto";
    ticks.push({ deg, at: (deg - COMPASS_FROM_DEG) / COMPASS_SPAN_DEG, label: kind === "cardeal" ? CARDINALS[norm]! : "", kind });
  }
  return ticks;
}

/**
 * Deslocamento da faixa (fração da largura dela, ≤ 0) para o rumo `heading` ficar no meio da janela. A
 * janela é um terço da faixa: com o rumo 0, a janela vai de −90° a 90°.
 */
export const compassOffset = (heading: number): number => -(wrapAngle(heading) * 180) / Math.PI / COMPASS_SPAN_DEG;

/** Nome do rumo para leitores de tela (o setor de 45° mais perto). */
export function headingName(heading: number): string {
  const names = ["norte", "nordeste", "leste", "sudeste", "sul", "sudoeste", "oeste", "noroeste"];
  return names[Math.round(wrapAngle(heading) / (Math.PI / 4)) % 8]!;
}

export interface CompassMark {
  /** Posição na janela, de −1 (borda esquerda) a 1 (direita). */
  x: number;
  /** Fora da janela: presa perto da borda, do lado mais curto para virar. */
  edge: boolean;
}

/**
 * Onde um alvo (aliado, ping, chefe) cai na bússola de quem está em (fromX, fromZ) olhando para `heading`.
 * Fora dos ±90° fica preso perto da borda (`COMPASS_EDGE`), do lado mais curto; em cima de você (menos de
 * 1 m), nada.
 */
export function compassMark(heading: number, fromX: number, fromZ: number, toX: number, toZ: number): CompassMark | undefined {
  const dx = toX - fromX;
  const dz = toZ - fromZ;
  if (dx * dx + dz * dz < 1) return undefined;
  const d = angleDiff(bearing(dx, dz), heading);
  const edge = Math.abs(d) > COMPASS_HALF;
  return { x: edge ? Math.sign(d) * COMPASS_EDGE : d / COMPASS_HALF, edge };
}
